'use client';

import '@/styles/global.css';

import { ErrorPage } from '@pickleballinc/features/src/error-pages/error-page';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { LEAGUE_LABELS } from '@pickleballinc/lib/product-labels';
import { Application } from '@pickleballinc/lib/types';
import { Button } from '@pickleballinc/ui/components/ui/button';
import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import * as Sentry from '@sentry/nextjs';
import type { Metadata } from 'next';
import { useEffect, useState } from 'react';

import { BackButton } from '@/components/Error/back-button';
import { publicEnv } from '@/lib/env';
import { getAppFromHostname } from '@/utils/helpers/getAppFromHostname';

export default function Error({
  error,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [application, setApplication] = useState<Application | undefined>(undefined);

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  useEffect(() => {
    setApplication(getAppFromHostname(window.location.hostname));
  }, []);

  return (
    <div className='flex size-full flex-1 flex-col text-gray-700 antialiased'>
      <main className='h-full bg-gray-100 py-4 sm:py-6'>
        <ErrorPage
          title='Page out of bounds.'
          description='We’re experiencing temporary issues. Please try again shortly.'
          application={application ?? 'pickleball'}
        >
          <BackButton />
          <CustomLink href='/'>
            <Button
              variant='default'
              className='w-full bg-experiment text-white hover:bg-experiment/80 md:w-32'
            >
              Back to home
            </Button>
          </CustomLink>
        </ErrorPage>
      </main>
    </div>
  );
}

export const metadata: Metadata = {
  metadataBase: new URL(`https://${publicEnv.NEXT_PUBLIC_HOME_ROOT_URL}`),
  title: 'Pickleball.com - All Things Pickleball.',
  description: `Pickleball.com is the place for live scores, latest news, video highlights, merchandise, tournaments, ${LEAGUE_LABELS.pluralLower}, clubs, ratings, and clinics`,
  keywords: `Pickleball News, Pro Pickleball, Tournament Site, Bracket Generator, Pickleball Tournaments, Pickleball Clinics, ${LEAGUE_LABELS.productSingular}, Pickleball Brackets, Elimination Bracket, Round Robin, Pool Play, Blind Draw, Singles, Doubles, Mix Doubles, Mens, Womens, Teams, Pickleball, Pickleball Paddle, Paddle, Pickleball Registrations, USAP, Pickleball Canada`,
  openGraph: {
    siteName: 'Pickleball.com',
    url: `https://${publicEnv.NEXT_PUBLIC_HOME_ROOT_URL}`,
    title: 'Pickleball.com | Official Site for all things Pickleball',
    description: `Pickleball.com is the place for live scores, latest news, video highlights, merchandise, tournaments, ${LEAGUE_LABELS.pluralLower}, clubs, ratings, and clinics`,
    images: [
      {
        url: getCDNURL(
          `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/opengraph/PB-opengraph.png`,
          1200,
          630
        ),
      },
    ],
    type: 'website',
  },
  twitter: {
    card: 'summary_large_image',
    site: 'pickleball',
    title: 'Pickleball.com | Official Site for all things Pickleball',
    description: `Pickleball.com is the place for live scores, latest news, video highlights, merchandise, tournaments, ${LEAGUE_LABELS.pluralLower}, clubs, ratings, and clinics`,
    images: [
      {
        url: getCDNURL(
          `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/opengraph/PB-opengraph.png`,
          1200,
          630
        ),
      },
    ],
  },
  icons: [
    {
      rel: 'apple-touch-icon',
      url: getCDNURL(
        `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/favicons/PB-apple-touch-icon-180x180.png`,
        180,
        180
      ),
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '32x32',
      url: getCDNURL(
        `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/favicons/PB-favicon-32x32.png`,
        32,
        32
      ),
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '16x16',
      url: getCDNURL(
        `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/favicons/PB-favicon-16x16.png`,
        16,
        16
      ),
    },
    {
      rel: 'icon',
      url: getCDNURL(
        `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/favicons/PB-favicon.ico`,
        512,
        512
      ),
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '192x192',
      url: getCDNURL(
        `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/favicons/PB-android-chrome-192x192.png`,
        192,
        192
      ),
    },
    {
      rel: 'icon',
      type: 'image/png',
      sizes: '512x512',
      url: getCDNURL(
        `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/favicons/PB-android-chrome-512x512.png`,
        512,
        512
      ),
    },
  ],
};
